/**
 * Settings > Activity privacy (2026-10-29), Discord's Activity Privacy and
 * Registered Games on one page: "Share my activity" (the account's
 * `show_current_game`, synced to every device), then in the desktop app the
 * game it sees now, the games you added or hid, and "Add it!" for a program
 * that runs now. The web app only explains that the desktop app detects.
 */
import type { DesktopGameEntry, DesktopGameState, DesktopProgram } from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { desktopBridge } from '../lib/config';
import { failToast } from '../mod/actions';
import { SetGroup, SetRow } from '../settings/Settings';
import { useFlag } from '../store/config';
import { updateData, useData } from '../store/data';
import { Btn, Icon, IconBtn, Switch } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { refreshGames, useGames } from './activity';

export function ActivitySection() {
  const { t } = useT();
  const share = useData((d) => d.settings?.show_current_game !== false);
  const known = useData((d) => !!d.settings);
  const [busy, setBusy] = useState(false);
  const on = useFlag('rich_presence');
  const setShare = async (v: boolean) => {
    setBusy(true);
    try {
      const s = await api.me.patchSettings({ show_current_game: v });
      updateData((d) => ({ ...d, settings: s }));
    } catch (e) {
      failToast(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h2>{t('nxs.act.title')}</h2>
      <SetGroup title={t('nxs.act.status')}>
        <SetRow label={t('nxs.act.share')} sub={t('nxs.act.shareSub')}>
          <Switch
            checked={share}
            disabled={busy || !known || !on}
            onChange={(v) => void setShare(v)}
            label={t('nxs.act.share')}
          />
        </SetRow>
      </SetGroup>
      <RegisteredGames share={share} />
    </>
  );
}

function RegisteredGames({ share }: { share: boolean }) {
  const { t } = useT();
  const bridge = desktopBridge();
  const state = useGames((s) => s.state);
  const current = useGames((s) => s.current);
  const [picking, setPicking] = useState(false);
  useEffect(() => {
    if (bridge?.games) void refreshGames();
  }, [bridge]);
  if (!bridge?.games) {
    return (
      <SetGroup title={t('nxs.act.games')}>
        <p className="nx-set-note">
          <Icon name="monitor" />
          <span>{t('nxs.act.webOnly')}</span>
        </p>
      </SetGroup>
    );
  }
  if (state && !state.supported) {
    return (
      <SetGroup title={t('nxs.act.games')}>
        <p className="nx-set-note">
          <Icon name="info" />
          <span>{t('nxs.act.unsupported')}</span>
        </p>
      </SetGroup>
    );
  }
  const apply = async (p: Promise<DesktopGameState>) => {
    try {
      const s = await p;
      useGames.setState({ state: s, current: s.current });
    } catch (e) {
      toast(typeof e === 'string' ? e : t('nxs.act.failed'), { tone: 'danger', icon: 'alert' });
    }
  };
  return (
    <SetGroup title={t('nxs.act.games')}>
      <div className="nx-set-row nxs-now" data-nxs-now="">
        <div className="nx-set-rt">
          <div className="l">{t('nxs.act.now')}</div>
          <div className="s" dir="auto">
            {current ? current.name : t('nxs.act.none')}
            {current && !share ? ' · ' + t('nxs.act.offNote') : ''}
          </div>
        </div>
        <span className={'nxs-now-dot' + (current ? ' on' : '')} aria-hidden="true" />
      </div>
      {(state?.games ?? []).map((g) => (
        <GameRow key={g.id} g={g} apply={apply} />
      ))}
      <p className="nx-set-note nxs-help">
        <span>{t('nxs.act.listHelp', { n: state?.builtinCount ?? 0 })}</span>
      </p>
      <div className="nx-set-row">
        <div className="nx-set-rt">
          <div className="l">{t('nxs.act.notSeeing')}</div>
        </div>
        <div className="nx-set-ctl">
          <Btn size="sm" icon="plus" onClick={() => setPicking(true)}>
            {t('nxs.act.addIt')}
          </Btn>
        </div>
      </div>
      {picking ? <AddGameDialog onClose={() => setPicking(false)} apply={apply} /> : null}
    </SetGroup>
  );
}

function GameRow({
  g,
  apply,
}: {
  g: DesktopGameEntry;
  apply: (p: Promise<DesktopGameState>) => Promise<void>;
}) {
  const { t } = useT();
  const games = desktopBridge()?.games;
  if (!games) return null;
  const flags = [
    g.running ? t('nxs.act.running') : null,
    g.hidden ? t('nxs.act.hidden') : null,
    g.custom ? t('nxs.act.custom') : null,
  ].filter(Boolean);
  return (
    <div className="nx-set-row nxs-game" data-game={g.id}>
      <div className="nx-set-rt">
        <div className="l" dir="auto">
          {g.name}
        </div>
        {flags.length ? <div className="s">{flags.join(' · ')}</div> : null}
      </div>
      <div className="nx-set-ctl">
        <IconBtn
          icon={g.hidden ? 'eye' : 'cm-eye-off'}
          label={g.hidden ? t('nxs.act.show') : t('nxs.act.hide')}
          pressed={g.hidden}
          onClick={() => void apply(games.setHidden(g.id, !g.hidden))}
        />
        {g.custom ? (
          <IconBtn
            icon="trash"
            label={t('nxs.act.remove', { name: g.name })}
            onClick={() => void apply(games.remove(g.id))}
          />
        ) : null}
      </div>
    </div>
  );
}

/** "Add it!": pick a running program with a window, then name it (its window title to start with). */
function AddGameDialog({
  onClose,
  apply,
}: {
  onClose(): void;
  apply: (p: Promise<DesktopGameState>) => Promise<void>;
}) {
  const { t } = useT();
  const games = desktopBridge()?.games;
  const [list, setList] = useState<DesktopProgram[] | null>(null);
  const [pick, setPick] = useState<DesktopProgram | null>(null);
  const [name, setName] = useState('');
  useEffect(() => {
    let live = true;
    games
      ?.listPrograms()
      .then((l) => live && setList(l))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [games]);
  if (!games) return null;
  const add = async () => {
    if (!pick || !name.trim()) return;
    await apply(games.add(pick.exe, name.trim()));
    toast(t('nxs.act.added', { name: name.trim() }), { icon: 'check', tone: 'ok' });
    onClose();
  };
  return (
    <Dialog
      title={t('nxs.act.addIt')}
      icon="plus"
      tone="mode"
      size="md"
      onClose={onClose}
      actions={
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" disabled={!pick || !name.trim()} onClick={() => void add()}>
            {t('nxs.act.addIt')}
          </Btn>
        </>
      }
    >
      <fieldset className="ns-field">
        <legend className="ns-label">{t('nxs.act.pick')}</legend>
        {list === null ? (
          <p className="ns-help" role="status">
            <i className="nx-spin" />
          </p>
        ) : list.length === 0 ? (
          <p className="ns-help">{t('nxs.act.pickNone')}</p>
        ) : (
          <div className="nxs-progs" role="radiogroup" aria-label={t('nxs.act.pick')}>
            {list.map((p) => (
              <button
                key={p.exe}
                type="button"
                role="radio"
                className="nxs-prog"
                aria-checked={pick?.exe === p.exe}
                onClick={() => {
                  setPick(p);
                  setName(p.title);
                }}
              >
                <b dir="auto">{p.title}</b>
                <small dir="ltr">{p.exe}</small>
              </button>
            ))}
          </div>
        )}
      </fieldset>
      {pick ? (
        <label className="ns-field">
          <span className="ns-label">{t('nxs.act.nameLabel')}</span>
          <input
            className="nx-input"
            dir="auto"
            maxLength={128}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
      ) : null}
    </Dialog>
  );
}
